<template>
  <div v-if="debt" class="p-3 flex flex-col gap-3">
    <div class="flex gap-1 items-baseline">
      <span class="font-semibold text-blue dark:text-white leading-none text-3xl">
        {{ totalDebt }}
      </span>
      <span>total debt</span>
    </div>
    <div class="flex-1 border-t pt-3 border-gray-300 dark:border-gray-800 rounded-b-lg whitespace-nowrap gap-x-8 gap-y-2 flex flex-wrap md:flex-col xl:flex-row">
      <div>
        <div class="text-xs">
          Monthly payment
          <term name="monthlyPayment"/>
        </div>
        <div class="flex gap-1 items-baseline">
          <span class="font-semibold text-blue dark:text-white text-lg">
            {{ monthlyPayment }}
          </span>
          <span v-if="debt.balance" class="text-xs">
            for {{ targetDebtPeriod }}y
          </span>
        </div>
      </div>
      <div>
        <div class="text-xs">
          Debt service
          <term
            v-if="medianEarnings && debt.balance"
            :text="`If you earn this school's median salary of ${currencyShort(medianEarnings)}, ${debtService}% will be used to pay off your loans for ${targetDebtPeriod} years.`"
          />
        </div>
        <div class="font-semibold text-blue dark:text-white text-lg">
          {{ debtService || "-" }}<span v-if="debtService">%</span>
        </div>
      </div>
      <div>
        <div class="text-xs">
          Target salary
          <template v-if="debt.target_salary">
            <term
              v-if="medianEarnings"
              :text="`To contribute ${targetTax}% of your salary to loan payments for ${targetDebtPeriod} years, you need a salary of ${currencyShort(debt.target_salary)} which is ${medianTargetSalaryDifferenceLabel} of this school's median salary of ${currencyShort(medianEarnings)}.`"
            />
            <term
              v-else
              :text="`To contribute ${targetTax}% of your salary to loan payments for ${targetDebtPeriod} years, you need a salary of ${currencyShort(debt.target_salary)}.`"
            />
          </template>
        </div>
        <div class="flex gap-1 items-baseline">
          <span class="font-semibold text-blue dark:text-white text-lg">{{ targetSalary }}/y</span>
          <span
            v-if="medianTargetSalaryDifferenceLabel"
            class="text-xs"
            :class="[medianTargetSalaryDifference <= 0 ? 'text-green' : 'text-red']"
          >
            {{ medianTargetSalaryDifferenceLabel }} median
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Term from "@/components/Term.vue"
import { computed } from "vue"
import { config, currency, currencyShort, percent } from "@/support"

const props = defineProps({
  debt: { type: Object },
  finances: { type: Object },
  medianEarnings: { type: Number },
})

const targetDebtPeriod = computed(() => props.finances?.target_debt_period || config("settings.budgets.default_target_debt_period"))
const targetTax = computed(() => props.finances?.target_tax || config("settings.budgets.default_tax"))
const totalDebt = computed(() => currency(props.debt.balance))
const debtService = computed(() => (props.medianEarnings ? percent(props.debt.monthly * 12, props.medianEarnings) : null))
const monthlyPayment = computed(() => currency(Math.round(props.debt[`monthly_${targetDebtPeriod.value}`])))
const targetSalary = computed(() => (props.debt.target_salary ? currencyShort(props.debt.target_salary) : "-"))

const medianTargetSalaryDifference = computed(() => (props.medianEarnings ? Math.round((props.debt.target_salary / props.medianEarnings * 100) - 100) : null))

const medianTargetSalaryDifferenceLabel = computed(() => {
  if (props.debt.target_salary && props.medianEarnings) {
    return medianTargetSalaryDifference.value <= 0
      ? `${medianTargetSalaryDifference.value}%`
      : `+${medianTargetSalaryDifference.value}%`
  }

  return null
})
</script>
